// src/components/shell/popovers/OrgPopover.tsx
//
// Sidebar rail entry for switching the active workspace organization.
// Resolves dev-sync note 6dabb83f-01db-4d46-8dbd-f697da132de5 (move the org
// selector out of the Admin panel + Account placeholder into a first-class
// sidebar item under Account).
//
// Visibility: the popover renders `null` when the user has access to only one
// org (the common case for sales/trainer users) so it never adds clutter for
// people who literally can't switch. See `useAccessibleOrgs`.
//
// Switch action: `useWorkspaceOrg().setWorkspaceOrganizationId(id)`. This is
// a localStorage-backed write that the auth/scoped-org plumbing already
// validates against memberships + platform roles. No page reload — every
// scoped-org consumer re-derives on the next render.

import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspaceOrg } from "@/contexts/WorkspaceOrgContext";
import { useAccessibleOrgs, type AccessibleOrg } from "@/hooks/useAccessibleOrgs";
import { SidebarRailButton } from "@/components/shell/Sidebar/SidebarRailButton";
import { railPopoverPanelStyle, useRailPopover } from "./useRailPopover";

export type OrgPopoverProps = {
  expanded: boolean;
  onAfterAction?: () => void;
};

function abbrev(name: string): string {
  const cleaned = name.trim();
  if (!cleaned) return "WO";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]!.charAt(0) + parts[1]!.charAt(0)).toUpperCase();
  }
  return parts[0]!.slice(0, 2).toUpperCase();
}

const CONFIRM_TAIL =
  "Admin, Knowledge Base (drafts and saves target this org unless you pick another scope), org branding, CRM tools, lead sync, workplace notifications, and Deals will use this org until you switch again.";

export function OrgPopover({ expanded, onAfterAction }: OrgPopoverProps) {
  const auth = useAuth();
  const { workspaceOrganizationId, setWorkspaceOrganizationId } = useWorkspaceOrg();
  const { orgs, loading, error, shouldShow } = useAccessibleOrgs();
  const { open, setOpen, toggle, triggerRef, panelRef, position } = useRailPopover();
  const [filter, setFilter] = useState("");

  const activeOrgId = workspaceOrganizationId ?? auth.activeOrganizationId ?? null;
  const activeOrg = useMemo<AccessibleOrg | null>(() => {
    if (!activeOrgId) return null;
    return orgs.find((o) => o.id === activeOrgId) ?? null;
  }, [orgs, activeOrgId]);

  // For platform admins the list can be long; let them type-to-filter.
  const isPlatformAdmin = auth.realHasRole("platform_admin");
  const filteredOrgs = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) => o.name.toLowerCase().includes(q));
  }, [orgs, filter]);

  if (!shouldShow) return null;

  const triggerLabel = "Workspace";
  const glyph = abbrev(activeOrg?.name ?? "WO");

  const closeAll = () => {
    setOpen(false);
    setFilter("");
    onAfterAction?.();
  };

  const switchTo = (next: AccessibleOrg) => {
    if (next.id === activeOrgId) {
      closeAll();
      return;
    }
    const confirmed = window.confirm(
      `Switch workspace organization to "${next.name}"?\n\n${CONFIRM_TAIL}`,
    );
    if (!confirmed) return;
    setWorkspaceOrganizationId(next.id);
    closeAll();
  };

  return (
    <>
      <SidebarRailButton
        ref={triggerRef}
        expanded={expanded}
        glyph={glyph}
        label={triggerLabel}
        glyphChipClassName="bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-100"
        active={open}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
      />
      {open
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label="Workspace organization"
              className="rounded-2xl border border-gray-200 bg-white text-gray-900 shadow-2xl dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
              style={railPopoverPanelStyle(position)}
            >
              <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Workspace organization
                  </div>
                  <div className="truncate text-sm font-semibold">
                    {activeOrg?.name ?? activeOrgId ?? "No active organization"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeAll}
                  className="-mr-1 rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              {isPlatformAdmin && orgs.length > 8 ? (
                <div className="px-3 pt-3">
                  <input
                    type="text"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter organizations"
                    className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500"
                  />
                </div>
              ) : null}

              <div className="px-2 py-2">
                {loading && orgs.length === 0 ? (
                  <div className="rounded-lg px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                    Loading organizations…
                  </div>
                ) : null}
                {error ? (
                  <div className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
                    {error}
                  </div>
                ) : null}
                {!loading && !error && orgs.length === 0 ? (
                  <div className="rounded-lg px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                    No accessible organizations.
                  </div>
                ) : null}

                <ul className="space-y-1" role="listbox" aria-label="Choose an organization">
                  {filteredOrgs.map((o) => {
                    const isActive = o.id === activeOrgId;
                    return (
                      <li key={o.id}>
                        <button
                          type="button"
                          onClick={() => switchTo(o)}
                          aria-current={isActive ? "true" : undefined}
                          role="option"
                          aria-selected={isActive}
                          className={
                            "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm " +
                            (isActive
                              ? "bg-indigo-50 font-semibold text-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-100"
                              : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800/70")
                          }
                        >
                          <span className="min-w-0 truncate">{o.name}</span>
                          {isActive ? (
                            <span aria-hidden className="shrink-0 text-xs text-indigo-700 dark:text-indigo-300">
                              ✓
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                  {!loading && !error && orgs.length > 0 && filteredOrgs.length === 0 ? (
                    <li className="rounded-lg px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                      No matches for &ldquo;{filter}&rdquo;.
                    </li>
                  ) : null}
                </ul>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
